import { courseRuntimeUrl } from './courseRuntimeContext';
import { getAuthToken } from '../../utils/auth';
import { dispatchUnauthorizedAuthEvent } from '../../utils/authSession';
import { API_BASE_URL } from '../../config/apiConfig';
import { fetchWithCookieCsrf } from '../csrf';

const STREAM_IDLE_TIMEOUT_MS = 45000;

/**
 * Stream an AI summary for an ai_summary question via SSE.
 * Returns an AbortController so the caller can cancel the stream.
 */
export const fetchStreamingAiSummary = (
  questionId: number,
  flowRunId: number | undefined,
  onChunk: (text: string) => void,
  onDone: (meta: { summary_type: string }) => void,
  onError: (err: Error) => void,
): AbortController => {
  const url = `${API_BASE_URL}${courseRuntimeUrl(`/api/questions/${questionId}/ai-summary/stream/`)}`;
  const controller = new AbortController();
  let finished = false;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  const resetIdleTimer = () => {
    if (idleTimer) {
      clearTimeout(idleTimer);
    }
    idleTimer = setTimeout(() => {
      if (finished) {
        return;
      }
      finished = true;
      controller.abort();
      onError(new Error('AI summary stream timed out while waiting for data'));
    }, STREAM_IDLE_TIMEOUT_MS);
  };
  const clearIdleTimer = () => {
    if (idleTimer) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  };
  resetIdleTimer();

  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (token) {
    headers.Authorization = `Token ${token}`;
  }


  fetchWithCookieCsrf(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(flowRunId != null ? { flow_run_id: flowRunId } : {}),
    signal: controller.signal,
    credentials: 'include',
  })
    .then(async (response) => {
      if (!response.ok) {
        if (response.status === 401) {
          dispatchUnauthorizedAuthEvent({
            reason: 'session_expired',
            status: 401,
            source: 'aiSummaryStream',
          });
        }
        throw new Error(`HTTP ${response.status}`);
      }
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('ReadableStream not supported');
      }
      const decoder = new TextDecoder();
      let buffer = '';
      const handleDataLine = (line: string) => {
        resetIdleTimer();
        if (!line.startsWith('data: ')) {
          return;
        }

        const jsonStr = line.slice(6);
        try {
          const parsed = JSON.parse(jsonStr);
          if (parsed.done) {
            finished = true;
            clearIdleTimer();
            onDone({ summary_type: parsed.summary_type });
            return;
          }

          if (parsed.chunk !== undefined) {
            onChunk(parsed.chunk);
            return;
          }

          if (parsed.error) {
            finished = true;
            clearIdleTimer();
            onError(new Error(parsed.error));
          }
        } catch {
          // Ignore malformed SSE lines
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          handleDataLine(line);
        }
      }

      const trailing = buffer.trim();
      if (trailing) {
        handleDataLine(trailing);
      }

      if (!finished) {
        clearIdleTimer();
        onError(new Error('AI summary stream closed before completion'));
      }
    })
    .catch((err) => {
      clearIdleTimer();
      if (err.name !== 'AbortError') {
        onError(err);
      }
    });

  return controller;
};
